import { useEffect, useMemo, useRef, useState } from "react";

/* =========================================================
   Shared business search brain.

   What this version fixes:
   - Loads every public /api/businesses pagination page, not only page 1.
   - Keeps the complete business object, including its unique id.
   - Searches nested details/details_json values as well as top-level fields.
   - Caches the approved list once per browser page load.
   ========================================================= */

const configuredApiBase =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const API_BASE_URL = (
  configuredApiBase || "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const PAGE_LIMIT = 200;
const MAX_TOTAL_PAGES = 100;

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

export const businessCategoryName = (business) =>
  firstText(
    business?.category,
    business?.api_category,
    business?.apiCategory,
    business?.business_type,
    business?.businessType,
    business?.type
  );

export const businessDisplayName = (business) =>
  firstText(
    business?.name,
    business?.business_name,
    business?.businessName
  );

const parseJsonObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string" || !value.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const businessDetails = (business) => {
  const directDetails = parseJsonObject(business?.details);
  const jsonDetails = parseJsonObject(business?.details_json);

  return {
    ...jsonDetails,
    ...directDetails,
  };
};

const primitiveSearchValues = (value, output = []) => {
  if (value === null || value === undefined) {
    return output;
  }

  if (typeof value === "string" || typeof value === "number") {
    output.push(String(value));
    return output;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => primitiveSearchValues(item, output));
    return output;
  }

  if (typeof value === "object") {
    Object.values(value).forEach((item) =>
      primitiveSearchValues(item, output)
    );
  }

  return output;
};

const businessSearchText = (business) => {
  const details = businessDetails(business);

  return primitiveSearchValues({
    ...business,
    details,
    // Avoid indexing the raw JSON string twice.
    details_json: undefined,
  })
    .join(" ")
    .toLowerCase();
};

const readBusinessList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.businesses)) {
    return payload.businesses;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const approvedOnly = (businesses) =>
  businesses.filter(
    (item) =>
      !item?.status || String(item.status).toLowerCase() === "approved"
  );

const uniqueBusinesses = (businesses) => {
  const unique = new Map();

  businesses.forEach((business) => {
    const details = businessDetails(business);
    const id = firstText(
      business?.id,
      business?.businessId,
      business?.business_id,
      details?.canonical_id
    );

    const fallbackKey = [
      businessDisplayName(business).toLowerCase(),
      firstText(business?.address, details?.address).toLowerCase(),
    ].join("|");

    const key = id || fallbackKey;

    if (key) {
      unique.set(key, business);
    }
  });

  return Array.from(unique.values());
};

const fetchBusinessPage = async (page) => {
  const url = new URL(`${API_BASE_URL}/api/businesses`);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(PAGE_LIMIT));
  url.searchParams.set("sort", "name");

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(
      `Business search request failed with status ${response.status}.`
    );
  }

  return response.json();
};

/* ---- one all-pages fetch per browser page load ---- */
let businessListPromise = null;

export const resetBusinessSearchCache = () => {
  businessListPromise = null;
};

export const loadApprovedBusinesses = () => {
  if (!businessListPromise) {
    businessListPromise = (async () => {
      try {
        const firstPayload = await fetchBusinessPage(1);
        const firstPageBusinesses = readBusinessList(firstPayload);

        const reportedTotalPages = Number(
          firstPayload?.pagination?.totalPages
        );

        const totalPages = Math.min(
          Math.max(
            Number.isFinite(reportedTotalPages) ? reportedTotalPages : 1,
            1
          ),
          MAX_TOTAL_PAGES
        );

        let allBusinesses = [...firstPageBusinesses];

        if (totalPages > 1) {
          const remainingPayloads = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              fetchBusinessPage(index + 2)
            )
          );

          allBusinesses = [
            ...allBusinesses,
            ...remainingPayloads.flatMap(readBusinessList),
          ];
        }

        return {
          ok: true,
          list: uniqueBusinesses(approvedOnly(allBusinesses)),
        };
      } catch (error) {
        console.warn(
          "GoOurHub search could not load approved businesses.",
          error
        );

        // Permit a later retry rather than permanently caching a failed request.
        businessListPromise = null;

        return { ok: false, list: [] };
      }
    })();
  }

  return businessListPromise;
};

const matchScore = (business, cleanedQuery, tokens) => {
  const name = businessDisplayName(business).toLowerCase();
  const category = businessCategoryName(business).toLowerCase();
  const haystack = businessSearchText(business);

  if (!tokens.every((token) => haystack.includes(token))) {
    return -1;
  }

  if (name === cleanedQuery) return 100;
  if (name.startsWith(cleanedQuery)) return 90;
  if (name.includes(cleanedQuery)) return 80;
  if (category.startsWith(cleanedQuery)) return 60;
  if (category.includes(cleanedQuery)) return 50;

  return 10;
};

/* ---- the hook ---- */
export function useBusinessSearch(query, options = {}) {
  const { minLength = 2, limit = 6 } = options;

  const [allBusinesses, setAllBusinesses] = useState([]);
  const [state, setState] = useState("idle");
  // "idle" | "loading" | "ready" | "unavailable"

  const requestedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const wanted = String(query || "").trim().length >= minLength;

  useEffect(() => {
    if (!wanted || requestedRef.current) {
      return;
    }

    requestedRef.current = true;
    setState("loading");

    loadApprovedBusinesses().then(({ ok, list }) => {
      if (!mountedRef.current) {
        return;
      }

      setAllBusinesses(list);
      setState(ok ? "ready" : "unavailable");

      if (!ok) {
        requestedRef.current = false;
      }
    });
  }, [wanted]);

  const matches = useMemo(() => {
    const cleaned = String(query || "").trim().toLowerCase();

    if (cleaned.length < minLength || allBusinesses.length === 0) {
      return [];
    }

    const tokens = cleaned.split(/\s+/).filter(Boolean);

    return allBusinesses
      .map((business) => ({
        business,
        score: matchScore(business, cleaned, tokens),
      }))
      .filter((entry) => entry.score >= 0)
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        return businessDisplayName(left.business).localeCompare(
          businessDisplayName(right.business)
        );
      })
      .slice(0, limit)
      .map((entry) => entry.business);
  }, [allBusinesses, query, minLength, limit]);

  return {
    matches,
    state,
    totalLoaded: allBusinesses.length,
  };
}
